import {
  AddOutlined,
  Checklist,
  CrisisAlert,
  Delete,
  DeleteOutline,
  Event as EventIcon,
  Flag,
  Image as ImageIcon,
  Remove,
  Repeat,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  FormControl,
  FormHelperText,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import {
  MobileDatePicker,
  MobileDateRangePicker,
} from "@mui/x-date-pickers-pro";
import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import dayjs from "dayjs";
import palette from "../../constants/palette";
import useDisclosure from "../../hooks/ui/useDisclosure";
import ModelAutoComplete from "../ModelAutoComplete";
import TagForm from "../TagForm";
import EditableTypography from "../EditableTypography";
import RRuleEditor, { defaultRRuleString } from "../RRuleEditor";
import api from "../../utils/api";
import { apiUrls } from "../../utils/urls";
import { useGet } from "../../hooks/api";
import { showErrorSnackbar, showSuccessSnackbar } from "../../utils/feedback";
import { sanitizeHtml } from "../../utils/formatting";
import ToDoEventForm from "../../../components/ToDoEventForm";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

export default function ToDoTemplateForm({
  row,
  onEdit,
  onCreate,
  initialToDoEvent = null,
  initialEventOccurrenceOffset = 0,
  readonly = false,
}) {
  const toDoTemplateId = row?.uuid;
  const isEventDisabled = !!initialToDoEvent;
  const { data } = useGet(apiUrls.admin.toDoTemplate(toDoTemplateId), {
    enabled: !!toDoTemplateId,
    staleTime: 0,
  });

  // form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [templateImages, setTemplateImages] = useState([]);
  const imageInputRef = useRef(null);

  const [repeat, setRepeat] = useState(false);
  const [recurrenceRule, setRecurrenceRule] = useState(defaultRRuleString());
  const [tempRecurrenceRule, setTempRecurrenceRule] = useState(
    defaultRRuleString()
  );
  const [repeatDateRange, setRepeatDateRange] = useState([null, null]);
  const repeatDisclosure = useDisclosure();
  const eventDisclosure = useDisclosure();

  const [hasTargetDate, setHasTargetDate] = useState(false);
  const [targetDate, setTargetDate] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const [hasChecklist, setHasChecklist] = useState(false);
  const [checklistItems, setChecklistItems] = useState([{ label: "" }]);
  const [previewChecklist, setPreviewChecklist] = useState(false);

  const [hasDeadline, setHasDeadline] = useState(false);
  const [deadline, setDeadline] = useState(null);

  const initialToDoEventOption = initialToDoEvent
    ? {
        label: initialToDoEvent.title,
        uuid: initialToDoEvent.uuid,
      }
    : null;
  const [tempToDoEvent, setTempToDoEvent] = useState(initialToDoEventOption);
  const [toDoEvent, setToDoEvent] = useState(initialToDoEventOption);
  const [eventOccurrenceOffset, setEventOccurrenceOffset] = useState(
    initialEventOccurrenceOffset
  );
  const [deadlineOffset, setDeadlineOffset] = useState(null);
  const [tags, setTags] = useState([]);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userProfiles, setUserProfiles] = useState([]);

  // Notification settings
  const [notify, setNotify] = useState(false);
  const [notifTeams, setNotifTeams] = useState([]);
  const [notifRoles, setNotifRoles] = useState([]);
  const [notifUsers, setNotifUsers] = useState([]);

  const [active, setActive] = useState(true);
  const [isQuestionFormat, setIsQuestionFormat] = useState(false);
  const [isCoordinated, setIsCoordinated] = useState(false);

  // Validation state
  const [deadlineOffsetError, setDeadlineOffsetError] = useState("");

  // Validation function for deadline offset
  const validateDeadlineOffset = (deadlineValue, eventOffsetValue) => {
    if (deadlineValue === null || deadlineValue === "") {
      setDeadlineOffsetError("");
      return true;
    }

    const deadlineNum = parseInt(deadlineValue);
    const eventOffsetNum = parseInt(eventOffsetValue || 0);

    if (deadlineNum < eventOffsetNum) {
      setDeadlineOffsetError(
        "Deadline must be equal to or after the event occurrence date"
      );
      return false;
    }

    setDeadlineOffsetError("");
    return true;
  };

  const iconSettingSx = (condition) => () => ({
    backgroundColor: condition ? palette.redRGBA(50) : undefined,
    "&:hover": { backgroundColor: condition ? palette.redRGBA(30) : undefined },
  });

  // load existing template
  useEffect(() => {
    if (toDoTemplateId) return;
    setTemplateImages([]);
  }, [toDoTemplateId]);

  useEffect(() => {
    if (!data) return;
    const t = data.data;
    setTitle(t.title || "");
    setDescription(t.description || "");
    setTemplateImages(
      (t.images || []).map((img) => ({
        ...img,
        action: null,
        file: null,
      }))
    );

    setRepeat(!!t.repeating);
    setRecurrenceRule(t.recurrence || defaultRRuleString());
    setTempRecurrenceRule(t.recurrence || defaultRRuleString());
    setRepeatDateRange([
      t.repeat_start_date ? dayjs(t.repeat_start_date) : null,
      t.repeat_end_date ? dayjs(t.repeat_end_date) : null,
    ]);

    setHasTargetDate(!!t.target_date);
    setTargetDate(t.target_date ? dayjs(t.target_date) : null);

    setHasChecklist(!!t.checklist_items?.length);
    setChecklistItems(t.checklist_items?.map((l) => ({ label: l })) || []);
    setPreviewChecklist(false);

    setHasDeadline(!!t.deadline);
    setDeadline(t.deadline ? dayjs(t.deadline) : null);

    setTags(t.tags || []);
    setTeams(t.teams || []);
    setRoles(t.roles || []);
    setUserProfiles(t.user_profiles || []);

    setNotify(t.notify);
    setNotifTeams(t.notify_teams || []);
    setNotifRoles(t.notify_roles || []);
    setNotifUsers(t.notify_user_profiles || []);
    const toDoEventVal = t.event
      ? {
          uuid: t.event.uuid,
          label: t.event.title,
        }
      : null;
    setToDoEvent(toDoEventVal);
    setTempToDoEvent(toDoEventVal);
    setEventOccurrenceOffset(t.event_occurrence_offset ?? 0);
    setDeadlineOffset(t.deadline_event_occurrence_offset ?? null);

    setActive(t.is_active);
    setIsQuestionFormat(t.is_question_format);
    setIsCoordinated(!!t.is_coordinated);
  }, [data]);

  // submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate deadline offset if event is set
    if (
      toDoEvent &&
      !validateDeadlineOffset(deadlineOffset, eventOccurrenceOffset)
    ) {
      return; // Stop submission if validation fails
    }

    let td = targetDate;
    if (!(repeat || targetDate)) td = dayjs();

    const payload = {
      title,
      description,
      repeat,
      rrule: repeat ? recurrenceRule : null,
      event_id: toDoEvent ? toDoEvent.uuid : null,
      event_occurrence_offset: toDoEvent
        ? parseInt(eventOccurrenceOffset)
        : null,
      target_date: td?.format("YYYY-MM-DD") || null,
      repeat_start_date: repeat
        ? repeatDateRange[0]?.format("YYYY-MM-DD") ?? null
        : null,
      repeat_end_date: repeat
        ? repeatDateRange[1]?.format("YYYY-MM-DD") ?? null
        : null,
      has_checklist: hasChecklist,
      checklist_items: checklistItems
        .filter((i) => i.label)
        .map((i) => i.label),
      deadline: hasDeadline && deadline ? deadline.format("YYYY-MM-DD") : null,
      deadline_event_occurrence_offset: deadlineOffset,
      tags: tags.map((t) => t.uuid),
      teams: teams.map((t) => t.uuid),
      roles: roles.map((r) => r.uuid),
      user_profiles: userProfiles.map((u) => u.uuid),
      is_active: active,
      is_question_format: isQuestionFormat,
      is_coordinated: isCoordinated,
      notify: notify,
      notify_teams: notifTeams.map((t) => t.uuid),
      notify_roles: notifRoles.map((t) => t.uuid),
      notify_user_profiles: notifUsers.map((t) => t.uuid),
    };

    try {
      const res = toDoTemplateId
        ? await api.put(apiUrls.admin.toDoTemplate(toDoTemplateId), payload)
        : await api.post(apiUrls.admin.toDoTemplates(), payload);

      let savedTemplate = res.data.data;
      const imagePayload = new FormData();
      const imageMetadata = [];
      let fileIndex = 0;

      const activeImages = templateImages.filter((img) => img.action !== "DELETE");
      const orderedByVisibility = activeImages.reduce((acc, image, index) => {
        acc[image.uuid || image.tempId] = index;
        return acc;
      }, {});

      templateImages.forEach((image) => {
        const ordering = orderedByVisibility[image.uuid || image.tempId] ?? 0;
        const metadata = {
          title: image.title || "",
          ordering,
        };

        if (image.file) {
          imagePayload.append("image_files", image.file);
          metadata.file_index = fileIndex;
          metadata.action = "CREATE";
          fileIndex += 1;
          imageMetadata.push(metadata);
          return;
        }

        if (!image.uuid) {
          return;
        }

        metadata.uuid = image.uuid;
        if (image.action === "DELETE") {
          metadata.action = "DELETE";
          imageMetadata.push(metadata);
          return;
        }

        if (image.action === "UPDATE") {
          metadata.action = "UPDATE";
          imageMetadata.push(metadata);
        }
      });

      if (imageMetadata.length > 0) {
        imagePayload.append("images", JSON.stringify(imageMetadata));
        const imagesRes = await api.post(
          apiUrls.admin.toDoTemplateImagesSync(savedTemplate.uuid),
          imagePayload,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        savedTemplate = imagesRes.data?.data || savedTemplate;
        setTemplateImages(
          (savedTemplate.images || []).map((img) => ({
            ...img,
            action: null,
            file: null,
          }))
        );
      }

      showSuccessSnackbar("Saved!");
      const cb = toDoTemplateId ? onEdit : onCreate;
      cb?.(savedTemplate);
    } catch {
      showErrorSnackbar(
        `Failed to ${toDoTemplateId ? "edit" : "create"} to-do item.`
      );
    }
  };

  const handleSelectImages = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }
    setTemplateImages((prev) => [
      ...prev,
      ...files.map((file) => ({
        tempId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        image: URL.createObjectURL(file),
        title: "",
        file,
        action: null,
      })),
    ]);
    event.target.value = "";
  };

  const handleDeleteImage = (image) => {
    if (image.uuid) {
      setTemplateImages((prev) =>
        prev.map((img) =>
          img.uuid === image.uuid ? { ...img, action: "DELETE" } : img
        )
      );
      return;
    }

    setTemplateImages((prev) =>
      prev.filter((img) => (img.uuid || img.tempId) !== (image.uuid || image.tempId))
    );
  };

  const handleImageTitleChange = (image, newTitle) => {
    setTemplateImages((prev) =>
      prev.map((img) => {
        if ((img.uuid || img.tempId) !== (image.uuid || image.tempId)) {
          return img;
        }
        const shouldMarkUpdate = img.uuid && !img.file && img.action !== "DELETE";
        return {
          ...img,
          title: newTitle,
          action: shouldMarkUpdate ? "UPDATE" : img.action,
        };
      })
    );
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ overflowY: "auto" }}>
        {/* Title */}
        <TextField
          sx={{ mt: 1 }}
          label="Title"
          size="small"
          fullWidth
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={readonly}
          slotProps={{
            input: {
              readOnly: readonly,
            },
          }}
        />

        {/* Description */}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Description
        </Typography>
        <Box
          sx={(theme) => {
            const color = theme.palette.text.primary;
            return {
              mt: 1,
              "& .ql-container": {
                minHeight: 120,
                borderColor: theme.palette.divider,
              },
              "& .ql-editor": {
                minHeight: 120,
                color,
              },
              "& .ql-editor.ql-blank::before": {
                color: theme.palette.text.disabled,
              },
              "& .ql-toolbar": {
                borderColor: theme.palette.divider,
              },
              "& .ql-toolbar .ql-stroke": {
                stroke: color,
              },
              "& .ql-toolbar .ql-fill": {
                fill: color,
              },
              "& .ql-toolbar .ql-picker-label": {
                color,
              },
              "& .ql-toolbar .ql-picker-options": {
                backgroundColor: theme.palette.background.paper,
              },
              "& .ql-toolbar .ql-picker-item": {
                color,
              },
            };
          }}
        >
          <ReactQuill
            theme="snow"
            value={description}
            onChange={setDescription}
            modules={QUILL_MODULES}
            readOnly={readonly}
            placeholder="Enter description…"
          />
        </Box>

        {/* Images */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Images</Typography>
          {!readonly && (
            <>
              <Button
                sx={{ mt: 1 }}
                variant="outlined"
                size="small"
                startIcon={<ImageIcon />}
                onClick={() => imageInputRef.current?.click()}
              >
                Add Images
              </Button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleSelectImages}
              />
            </>
          )}
          <Stack sx={{ mt: 1.5 }} spacing={1}>
            {templateImages
              .filter((img) => img.action !== "DELETE")
              .map((image, index) => (
                <Card key={image.uuid || image.tempId} variant="outlined">
                  <Stack direction="row">
                    <CardMedia
                      component="img"
                      image={image.image}
                      alt={image.title || `To-do image ${index + 1}`}
                      sx={{ width: 96, height: 96, objectFit: "cover" }}
                    />
                    <CardContent sx={{ flexGrow: 1, py: 1.25 }}>
                      <TextField
                        label="Image title"
                        size="small"
                        fullWidth
                        value={image.title || ""}
                        onChange={(event) =>
                          handleImageTitleChange(image, event.target.value)
                        }
                        disabled={readonly}
                      />
                      {!readonly && (
                        <Button
                          sx={{ mt: 1 }}
                          size="small"
                          color="error"
                          startIcon={<DeleteOutline />}
                          onClick={() => handleDeleteImage(image)}
                        >
                          Remove
                        </Button>
                      )}
                    </CardContent>
                  </Stack>
                </Card>
              ))}
          </Stack>
        </Box>

        {/* Tags */}
        <ModelAutoComplete
          sx={{ mt: 2, mb: 3 }}
          modelName="Tag"
          label="Tags"
          allowCreation={!readonly}
          CreationForm={TagForm}
          size="small"
          variant="outlined"
          multiple
          value={tags}
          setValue={setTags}
          disabled={readonly}
        />

        {/* Icon Controls */}
        <Stack direction="row" gap={1} mt={2} justifyContent="center">
          <Tooltip title="Event">
            <IconButton
              color="primary"
              onClick={eventDisclosure.onOpen}
              disabled={hasTargetDate || repeat}
              sx={iconSettingSx(toDoEvent)}
            >
              <EventIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Repeat">
            <IconButton
              color="primary"
              onClick={repeatDisclosure.onOpen}
              disabled={hasTargetDate || !!toDoEvent}
              sx={iconSettingSx(repeat)}
            >
              <Repeat />
            </IconButton>
          </Tooltip>
          <Tooltip title="Target Date">
            <IconButton
              color="primary"
              disabled={readonly || repeat || !!toDoEvent}
              onClick={
                readonly
                  ? undefined
                  : () => {
                      if (hasTargetDate) {
                        setHasTargetDate(false);
                      } else {
                        setDatePickerOpen(true);
                      }
                    }
              }
              sx={iconSettingSx(hasTargetDate)}
            >
              <CrisisAlert />
            </IconButton>
          </Tooltip>
          <Tooltip title="Checklist">
            <IconButton
              color="primary"
              onClick={
                readonly
                  ? undefined
                  : () => {
                      setHasChecklist((v) => !v);
                      if (!hasChecklist) setChecklistItems([{ label: "" }]);
                    }
              }
              disabled={readonly}
              sx={iconSettingSx(hasChecklist)}
            >
              <Checklist />
            </IconButton>
          </Tooltip>
          <Tooltip title="Deadline">
            <IconButton
              color="primary"
              onClick={readonly ? undefined : () => setHasDeadline((v) => !v)}
              disabled={readonly || !!toDoEvent}
              sx={iconSettingSx(hasDeadline)}
            >
              <Flag />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Checklist + preview toggle */}
        {hasChecklist && (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 2 }}
            >
              <Typography variant="overline">Checklist items</Typography>
              <FormControlLabel
                sx={{ m: 0 }}
                label="Preview"
                labelPlacement="start"
                control={
                  <Switch
                    size="small"
                    checked={previewChecklist}
                    onChange={(_, v) => setPreviewChecklist(v)}
                    disabled={readonly}
                  />
                }
              />
            </Stack>

            {previewChecklist ? (
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  backgroundColor: (theme) => theme.palette.background.paper,
                }}
              >
                {checklistItems.filter((i) => i.label).length ? (
                  checklistItems
                    .filter((i) => i.label)
                    .map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Typography sx={{ mr: 1 }}>{idx + 1}.</Typography>
                        {sanitizeHtml(item.label)}
                      </Box>
                    ))
                ) : (
                  <Typography color="textSecondary">
                    No checklist items to preview
                  </Typography>
                )}
              </Box>
            ) : (
              <List>
                {checklistItems.map((item, idx) => (
                  <ListItem
                    key={idx}
                    divider
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Typography
                      sx={{
                        mr: 1,
                        backgroundColor: "background.primary",
                        width: "24px",
                        height: "24px",
                        px: 1,
                        textAlign: "center",
                        borderRadius: "50%",
                      }}
                    >
                      {idx + 1}
                    </Typography>
                    <EditableTypography
                      value={item.label}
                      placeholder="Enter label"
                      fullWidth
                      multiline
                      required
                      onChange={
                        readonly
                          ? undefined
                          : (val) => {
                              const u = [...checklistItems];
                              u[idx].label = val;
                              setChecklistItems(u);
                            }
                      }
                      disabled={readonly}
                    />
                    {!readonly && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setChecklistItems((ci) => {
                              const u = [...ci];
                              u.splice(idx + 1, 0, { label: "" });
                              return u;
                            })
                          }
                        >
                          <AddOutlined />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setChecklistItems((ci) => {
                              const u = [...ci];
                              u.splice(idx, 1);
                              return u;
                            })
                          }
                        >
                          <Remove />
                        </IconButton>
                      </>
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        {/* Deadline */}
        {hasDeadline && (
          <Box mt={2}>
            <Typography variant="overline">Deadline</Typography>
            <MobileDatePicker
              value={deadline}
              disablePast
              onChange={setDeadline}
              disabled={readonly}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  disabled: readonly,
                },
              }}
            />
          </Box>
        )}

        {/* Target Audience */}
        <Accordion sx={{ mt: 2 }} elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="overline">Target Audience</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" fontSize="small" mb={1}>
              If the audience is left empty, the item will be assigned to you.
            </Typography>
            <ModelAutoComplete
              modelName="Team"
              label="Teams"
              variant="outlined"
              size="small"
              multiple
              value={teams}
              setValue={setTeams}
              disabled={readonly}
            />
            <ModelAutoComplete
              sx={{ mt: 2 }}
              modelName="Role"
              label="Roles"
              variant="outlined"
              size="small"
              multiple
              value={roles}
              setValue={setRoles}
              disabled={readonly}
            />
            <ModelAutoComplete
              sx={{ mt: 2 }}
              modelName="UserProfile"
              label="Users"
              variant="outlined"
              size="small"
              multiple
              value={userProfiles}
              setValue={setUserProfiles}
              disabled={readonly}
            />
          </AccordionDetails>
        </Accordion>
        {/* Notifications */}
        <Accordion elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="overline">Notifications</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControl sx={{ m: 0, mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notify}
                    onChange={(_, v) => setNotify(v)}
                    color="secondary"
                    disabled={readonly}
                  />
                }
                label="Send notification on completion"
              />
            </FormControl>
            {notify && (
              <Box>
                <Typography variant="subtitle2" fontSize="small" mb={1}>
                  If the audience is left empty, you will be notified
                </Typography>
                <Typography variant="overline" fontSize="small" mb={1}>
                  Notification audience
                </Typography>
                <ModelAutoComplete
                  modelName="Team"
                  label="Teams"
                  variant="outlined"
                  size="small"
                  multiple
                  value={notifTeams}
                  setValue={setNotifTeams}
                  disabled={readonly}
                />
                <ModelAutoComplete
                  sx={{ mt: 2 }}
                  modelName="Role"
                  label="Roles"
                  variant="outlined"
                  size="small"
                  multiple
                  value={notifRoles}
                  setValue={setNotifRoles}
                  disabled={readonly}
                />
                <ModelAutoComplete
                  sx={{ mt: 2 }}
                  modelName="UserProfile"
                  label="Users"
                  variant="outlined"
                  size="small"
                  multiple
                  value={notifUsers}
                  setValue={setNotifUsers}
                  disabled={readonly}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Additional Settings */}
        <Accordion elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="overline">Additional Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControl sx={{ m: 0 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={(_, v) => setActive(v)}
                    color="secondary"
                    disabled={readonly}
                  />
                }
                label="Active"
              />
              <FormHelperText>
                If inactive, hidden from future to-do lists until reactivated.
              </FormHelperText>
            </FormControl>
            <FormControl sx={{ m: 0, mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isQuestionFormat}
                    onChange={(_, v) => setIsQuestionFormat(v)}
                    color="secondary"
                    disabled={readonly}
                  />
                }
                label="Question Format"
              />
              <FormHelperText>Use yes/no instead of a checkbox.</FormHelperText>
            </FormControl>
            <FormControl sx={{ m: 0, mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isCoordinated}
                    onChange={(_, v) => setIsCoordinated(v)}
                    color="secondary"
                    disabled={readonly}
                  />
                }
                label="Coordinated item"
              />
              <FormHelperText>
                Completing this item marks the same coordination group complete for
                everyone. Checklist progress is also shared across the group.
              </FormHelperText>
            </FormControl>
          </AccordionDetails>
        </Accordion>

        {/* Submit */}
        {!readonly && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            <Button type="submit">{toDoTemplateId ? "Save" : "Create"}</Button>
          </Box>
        )}
      </Box>

      {/* Repeat Rule Dialog */}
      <Dialog {...repeatDisclosure.getDisclosureProps()} maxWidth="xs">
        <DialogContent>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="overline">Repeat</Typography>
            <Button
              size="small"
              endIcon={<Delete />}
              onClick={() => {
                setRepeat(false);
                setRecurrenceRule(null);
                setRepeatDateRange([null, null]);
                repeatDisclosure.onClose();
              }}
              disabled={readonly}
            >
              Delete Repetition
            </Button>
          </Box>
          <RRuleEditor
            value={tempRecurrenceRule}
            onChange={setTempRecurrenceRule}
            disabled={readonly}
          />
          <Box mt={2}>
            <Typography variant="overline">Date Range (Optional)</Typography>
            <MobileDateRangePicker
              value={repeatDateRange}
              disablePast
              onChange={setRepeatDateRange}
              disabled={readonly}
              slotProps={{ textField: { size: "small", disabled: readonly } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={repeatDisclosure.onClose}>
            {readonly ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={() => {
              setRepeat(true);
              setRecurrenceRule(tempRecurrenceRule);
              repeatDisclosure.onClose();
            }}
            disabled={readonly}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Dialog */}
      <Dialog {...eventDisclosure.getDisclosureProps()} maxWidth="xs" fullWidth>
        <Box
          component={"form"}
          onSubmit={(event) => {
            event.preventDefault();
            setToDoEvent(tempToDoEvent);
            eventDisclosure.onClose();
          }}
          sx={{ width: "100%" }}
        >
          <DialogContent>
            <ModelAutoComplete
              sx={{ mt: 2, mb: 3 }}
              modelName="ToDoEvent"
              label="Event"
              allowCreation={!readonly}
              CreationForm={ToDoEventForm}
              size="small"
              variant="outlined"
              value={tempToDoEvent}
              setValue={setTempToDoEvent}
              required
              disabled={readonly || isEventDisabled}
            />
            <TextField
              type="number"
              label="Event Occurrence Offset"
              value={eventOccurrenceOffset}
              onChange={(event) => {
                const newValue = event.target.value;
                setEventOccurrenceOffset(newValue);
                // Revalidate deadline offset when event occurrence offset changes
                validateDeadlineOffset(deadlineOffset, newValue);
              }}
              helperText="Integer representing the offset in days relative to the event in which this to-do item will occur. Examples: '-1' is one day before, '2' is two days after. '0' is the date of the event."
              required
              disabled={readonly}
              slotProps={{
                input: {
                  readOnly: readonly,
                },
              }}
              sx={{ mb: 2 }}
              fullWidth
            />
            <TextField
              type="number"
              label="Deadline Offset (Optional)"
              value={deadlineOffset ?? ""}
              onChange={(event) => {
                const newValue = event.target.value || null;
                setDeadlineOffset(newValue);
                validateDeadlineOffset(newValue, eventOccurrenceOffset);
              }}
              error={!!deadlineOffsetError}
              helperText={
                deadlineOffsetError ||
                "Optional: Days relative to the event when this to-do item is due. Must be equal to or after the event occurrence offset. Examples: '-1' means due one day before the event, '2' means due two days after. Leave blank for no deadline."
              }
              disabled={readonly}
              slotProps={{
                input: {
                  readOnly: readonly,
                },
              }}
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                if (toDoEvent) {
                  setToDoEvent(null);
                  setEventOccurrenceOffset(0);
                }
                eventDisclosure.onClose();
              }}
              disabled={readonly || isEventDisabled}
            >
              {toDoEvent ? "Remove Event" : "Cancel"}
            </Button>
            <Button type="submit" disabled={readonly || !!deadlineOffsetError}>
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Date Picker Pop-up */}
      <MobileDatePicker
        disablePast
        open={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        value={targetDate}
        onChange={(newVal) => {
          setTargetDate(newVal);
          setHasTargetDate(true);
        }}
        slotProps={{ textField: { sx: { display: "none" } } }}
      />
    </>
  );
}
